import { ConvexAuthProvider } from '@convex-dev/auth/react';
import { ConvexQueryClient } from '@convex-dev/react-query';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { ContainerProvider } from '@repo/core';
import * as Sentry from '@sentry/react';
import { ConvexReactClient } from 'convex/react';
import { routeTree } from './routeTree.gen';
import { createAppContainer } from './di/container';
import { env } from './env';
import { toUserFriendlyErrorMessage } from './lib/errors';

// ─── Convex + TanStack Query wiring ──────────────────────────────────────────
const convex = new ConvexReactClient(env.VITE_CONVEX_URL);
const convexQueryClient = new ConvexQueryClient(convex);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Wire Convex queries into TanStack Query
      queryKeyHashFn: convexQueryClient.hashFn(),
      queryFn: convexQueryClient.queryFn(),
      staleTime: 1000 * 60 * 5, // 5 min
      retry: (failureCount, error) => {
        // Don't retry Convex permission/not-found errors
        if (error instanceof Error && error.message.includes('Convex')) return false;
        return failureCount < 2;
      },
    },
  },
});

// Keep Convex + QueryClient in sync
convexQueryClient.connect(queryClient);

// ─── Router ───────────────────────────────────────────────────────────────────
const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

// ─── DI container ────────────────────────────────────────────────────────────
const container = createAppContainer();

// ─── App component ───────────────────────────────────────────────────────────
export default function App() {
  return (
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => (
        <div className="flex min-h-screen flex-col items-center justify-center gap-4 p-4 text-center">
          <h1 className="text-2xl font-bold text-red-600">Something went wrong</h1>
          <p className="max-w-md text-sm text-default-500">
            {toUserFriendlyErrorMessage(
              error,
              'We hit a problem loading this page. Please try again.',
            )}
          </p>
          <button
            type="button"
            onClick={resetError}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-white"
          >
            Try again
          </button>
        </div>
      )}
    >
      <ConvexAuthProvider client={convex}>
        <ContainerProvider container={container}>
          <QueryClientProvider client={queryClient}>
            <RouterProvider router={router} />
          </QueryClientProvider>
        </ContainerProvider>
      </ConvexAuthProvider>
    </Sentry.ErrorBoundary>
  );
}
